// ─── Build Checklist — Static Step Definitions ────────────────────────────────
// All three phases with subgroups, batch flags, commands, critical/required flags.

export const PHASES = [
  {
    id: 'setup',
    label: 'Phase 1: Setup',
    subgroups: [
      {
        id: 'preflight',
        label: 'Pre-Flight',
        steps: [
          { id: 'preflight_password_mgr',   label: 'Set up password manager',            batch: false, command: null,  critical: false, required: false },
          { id: 'preflight_agent_email',     label: 'Create agent email account',                            batch: false, command: null,  critical: false, required: false },
          { id: 'preflight_apple_id',        label: 'Create Apple ID (using agent email)',                    batch: false, command: 'appleid.apple.com — create with agent email, store password immediately', critical: false, required: false },
          { id: 'preflight_gmail_infra',     label: 'Configure Gmail infrastructure',                        batch: false, command: null,  critical: false, required: false },
          { id: 'preflight_anthropic_key',   label: 'Generate Anthropic API key',         batch: false, command: null,  critical: false, required: false },
          { id: 'preflight_brave_key',       label: 'Generate Brave Search API key',      batch: true,  command: null,  critical: false, required: false },
          { id: 'preflight_telegram_bot',    label: 'Create Telegram bot via @BotFather (/newbot, set name + photo, store token)', batch: false, command: 'Open @BotFather in Telegram → /newbot → set display name → copy bot token → store in credential_values', critical: false, required: false },
          { id: 'preflight_spending_limits', label: 'Set API spending limits',            batch: false, command: null,  critical: false, required: false },
        ],
      },
      {
        id: 'hardware_setup',
        label: 'Hardware Setup',
        steps: [
          { id: 'hw_serial_assigned',  label: 'Assign serial number to build',          batch: false, command: null,                                                                      critical: false, required: true  },
          { id: 'hw_postit',           label: 'Label hardware with post-it',             batch: false, command: null,                                                                      critical: false, required: false },
          { id: 'hw_erase',            label: 'Erase All Content and Settings',          batch: true,  command: 'System Settings → General → Transfer or Reset → Erase All Content and Settings', critical: false, required: false },
          { id: 'hw_setup_wifi',       label: 'Connect to Wi-Fi',                        batch: true,  command: null,                                                                      critical: false, required: false },
          { id: 'hw_apple_id',         label: 'Sign in with Apple ID',                   batch: false, command: null,                                                                      critical: false, required: false },
          { id: 'hw_admin_account',    label: 'Create admin account',                    batch: true,  command: null,                                                                      critical: false, required: false },
          { id: 'hw_filevault',        label: 'Enable FileVault encryption',             batch: true,  command: null,                                                                      critical: false, required: false },
          { id: 'hw_decline_services', label: 'Decline unnecessary Apple services',      batch: true,  command: null,                                                                      critical: false, required: false },
          { id: 'hw_touch_id',         label: 'Touch ID — SKIP during build (client sets up at delivery)', batch: false, command: null, critical: false, required: false },
        ],
      },
      {
        id: 'os_core_tools',
        label: 'OS & Core Tools',
        steps: [
          { id: 'os_update',       label: 'Run software update',      batch: true,  command: 'System Settings → Software Update',                                                                    critical: false, required: false },
          { id: 'os_energy',       label: 'Configure energy settings', batch: true,  command: 'System Settings → Energy',                                                                             critical: false, required: false },
          { id: 'os_amphetamine',  label: 'Install Amphetamine',       batch: true,  command: null, critical: false, required: false },
          { id: 'os_power_protect', label: 'Install Power Protect (Amphetamine prompts automatically — MacBooks only)', batch: true, command: null, critical: false, required: false },
          { id: 'tools_xcode',     label: 'Install Xcode CLI tools',   batch: true,  command: 'xcode-select --install',                                                                               critical: false, required: false },
          { id: 'tools_homebrew',  label: 'Install Homebrew',          batch: true,  command: '/bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"',    critical: false, required: false },
          { id: 'tools_node',      label: 'Install Node.js 22',        batch: true,  command: 'brew install node@22',                                                                                 critical: false, required: false },
          { id: 'tools_pnpm',      label: 'Install pnpm',              batch: true,  command: 'npm install -g pnpm',                                                                                  critical: false, required: false },
        ],
      },
      {
        id: 'openclaw_install',
        label: 'OpenClaw Installation',
        steps: [
          { id: 'oc_install',         label: 'Install OpenClaw',               batch: true,  command: 'npm install -g openclaw@latest',                        critical: false, required: false },
          { id: 'oc_skills',          label: 'Install standard skills (openclaw configure → Skills only)', batch: true, command: 'openclaw configure\n# Select "Skills only"\n# Install: apple-notes, blogwatcher, clawhub, gemini, gifgrep, github, gog, goplaces, himalaya, imsg, model-usage, nano-banana-pro, nano-pdf, openai-whisper, peekaboo, sag, songsee, summarize, video-frames, xurl\n# Skip skills that prompt for API keys (client provides via intake)\n# Verify keyless HTTP fetch is ON', critical: false, required: false },
          { id: 'oc_onboard',         label: 'Run OpenClaw onboard',           batch: false, command: 'openclaw onboard --install-daemon',                     critical: false, required: false },
          { id: 'oc_heartbeat',       label: 'Configure heartbeat',            batch: true,  command: null,                                                     critical: false, required: false },
          { id: 'oc_memory_flush',    label: 'Flush memory',                   batch: true,  command: null,                                                     critical: false, required: false },
          { id: 'oc_web_search',      label: 'Enable web search',              batch: false, command: null,                                                     critical: false, required: false },
          { id: 'oc_telegram_pair',   label: 'Pair Telegram bot',              batch: false, command: null,                                                     critical: false, required: false },
          { id: 'oc_telegram_allow',  label: 'Allow Telegram bot',             batch: false, command: null,                                                     critical: false, required: false },
          { id: 'oc_verify',          label: 'Verify gateway status',          batch: false, command: 'openclaw gateway status && openclaw health',             critical: false, required: false },
          { id: 'oc_security_audit',  label: 'Run security audit',             batch: false, command: 'openclaw security audit --deep',                        critical: false, required: false },
        ],
      },
    ],
  },
  {
    id: 'customization',
    label: 'Phase 2: Customization',
    subgroups: [
      {
        id: 'security_hardening',
        label: 'Security Hardening',
        steps: [
          { id: 'sec_standard_user',   label: 'Create standard user account',              batch: false, command: null,                                                     critical: false, required: false },
          { id: 'sec_remove_admin',    label: 'Remove admin privileges from user account', batch: false, command: null,                                                     critical: false, required: false },
          { id: 'sec_remote_login',    label: 'Enable remote login (Tailscale only)',       batch: false, command: 'sudo systemsetup -f -setremotelogin on',                 critical: false, required: false },
          { id: 'sec_tailscale',       label: 'Install Tailscale',                         batch: false, command: null,                                                     critical: false, required: false },
          { id: 'sec_tailscale_access', label: 'Configure Tailscale access controls',     batch: false, command: null,                                                     critical: false, required: false },
          { id: 'sec_lock_configs',    label: 'Lock OpenClaw config file permissions',     batch: true,  command: 'chmod 700 ~/.openclaw && chmod 600 ~/.openclaw/openclaw.json', critical: false, required: false },
          { id: 'sec_gateway_auth',    label: 'Enable gateway authentication',             batch: false, command: null,                                                     critical: false, required: false },
          { id: 'sec_firewall',        label: 'Configure macOS firewall',                 batch: true,  command: 'System Settings → Network → Firewall → ON\n• Automatically allow built-in software: OFF\n• Automatically allow downloaded signed software: OFF\n• Stealth mode: ON\n• Control Centre: Block (deny when prompted)', critical: false, required: false },
        ],
      },
      {
        id: 'agent_identity',
        label: 'Agent Identity',
        steps: [
          { id: 'cfg_soul',            label: 'Upload SOUL.md config',            batch: false, command: null,                        critical: false, required: false },
          { id: 'cfg_identity',        label: 'Upload IDENTITY.md config',        batch: false, command: null,                        critical: false, required: false },
          { id: 'cfg_agents',          label: 'Upload AGENTS.md config',          batch: false, command: null,                        critical: false, required: false },
          { id: 'cfg_heartbeat',       label: 'Upload HEARTBEAT.md config',       batch: false, command: null,                        critical: false, required: false },
          { id: 'cfg_user',            label: 'Upload USER.md config',            batch: false, command: null,                        critical: false, required: false },
          { id: 'cfg_tools',           label: 'Upload TOOLS.md config',           batch: false, command: null,                        critical: false, required: false },
          { id: 'cfg_env',             label: 'Configure environment variables',  batch: false, command: null,                        critical: false, required: false },
          { id: 'cfg_restart_verify',  label: 'Restart gateway and verify',       batch: false, command: 'openclaw gateway restart',  critical: false, required: false },
        ],
      },
      {
        id: 'telegram_config',
        label: 'Telegram Config',
        steps: [
          { id: 'tg_allowlist',    label: 'Add client to Telegram allowlist', batch: false, command: null, critical: false, required: false },
          { id: 'tg_dm_policy',    label: 'Set DM policy',                    batch: false, command: null, critical: false, required: false },
          { id: 'tg_client_test',  label: 'Send client test message',         batch: false, command: null, critical: false, required: false },
        ],
      },
      {
        id: 'integrations',
        label: 'Integrations',
        steps: [
          { id: 'int_1', label: 'Integration 1 (mark N/A if not needed)', batch: false, command: null, critical: false, required: false },
          { id: 'int_2', label: 'Integration 2 (mark N/A if not needed)', batch: false, command: null, critical: false, required: false },
          { id: 'int_3', label: 'Integration 3 (mark N/A if not needed)', batch: false, command: null, critical: false, required: false },
        ],
      },
      {
        id: 'knowledge_base',
        label: 'Knowledge Base',
        steps: [
          { id: 'kb_docs', label: 'Upload knowledge base documents', batch: false, command: null, critical: false, required: false },
        ],
      },
      {
        id: 'final_verification',
        label: 'Final Verification',
        steps: [
          { id: 'verify_doctor',    label: 'Run openclaw doctor',          batch: true,  command: 'openclaw doctor',   critical: false, required: false },
          { id: 'verify_queries',   label: 'Test sample queries',           batch: false, command: null,                critical: false, required: false },
          { id: 'verify_heartbeat', label: 'Verify heartbeat is live',      batch: false, command: null,                critical: false, required: false },
          { id: 'verify_status',    label: 'Check openclaw status',         batch: true,  command: 'openclaw status',   critical: false, required: false },
          { id: 'verify_ready',     label: 'Mark build ready for delivery', batch: false, command: null,                critical: false, required: false },
        ],
      },
    ],
  },
  {
    id: 'delivery',
    label: 'Phase 3: Delivery',
    subgroups: [
      {
        id: 'before_call',
        label: 'Before Call',
        steps: [
          { id: 'del_zip',              label: 'Generate and send build ZIP',       batch: false, command: null, critical: false, required: false },
          { id: 'del_client_ready',     label: 'Confirm client is ready for call',  batch: false, command: null, critical: false, required: false },
          { id: 'del_screenshare',      label: 'Set up screen share link',          batch: false, command: null, critical: false, required: false },
          { id: 'del_status_deploying', label: 'Mark status as Deploying',          batch: false, command: null, critical: false, required: false },
        ],
      },
      {
        id: 'demos',
        label: 'Demos',
        steps: [
          { id: 'del_first_convo',    label: 'Demo first conversation',      batch: false, command: null, critical: false, required: false },
          { id: 'del_demo_briefing',  label: 'Demo daily briefing',          batch: false, command: null, critical: false, required: false },
          { id: 'del_demo_crm',       label: 'Demo CRM workflow',            batch: false, command: null, critical: false, required: false },
          { id: 'del_demo_priority',  label: 'Demo priority escalation',     batch: false, command: null, critical: false, required: false },
          { id: 'del_questions',      label: 'Answer client questions',      batch: false, command: null, critical: false, required: false },
        ],
      },
      {
        id: 'handoff',
        label: 'Handoff — CRITICAL',
        steps: [
          { id: 'hand_apple_id',         label: 'Transfer Apple ID trusted device',     batch: false, command: 'Settings → Apple ID → Password & Security → Edit trusted phone numbers', critical: true, required: false },
          { id: 'hand_telegram_bot',     label: 'Transfer Telegram bot ownership',      batch: false, command: 'BotFather → /mybots → select bot → Bot Settings → Transfer Ownership',  critical: true, required: false },
          { id: 'hand_gateway_token',    label: 'Hand over gateway token',              batch: false, command: null,                                                                      critical: true, required: false },
          { id: 'hand_winning_wed',      label: 'Enroll in Winning Wednesday',          batch: false, command: null,                                                                      critical: true, required: false },
          { id: 'hand_independent_test', label: 'Client runs independent test',         batch: false, command: null,                                                                      critical: true, required: false },
          { id: 'hand_remove_adam',      label: 'Remove Adam from agent access',        batch: false, command: null,                                                                      critical: true, required: false },
        ],
      },
      {
        id: 'post_delivery',
        label: 'Post-Delivery',
        steps: [
          { id: 'hand_mark_deployed', label: 'Mark build as Deployed',      batch: false, command: null, critical: false, required: false },
          { id: 'post_update_record', label: 'Update client record',         batch: false, command: null, critical: false, required: false },
          { id: 'post_notify_vito',   label: 'Notify Vito of deployment',   batch: false, command: null, critical: false, required: false },
          { id: 'post_hw_confirm',    label: 'Confirm hardware in field',    batch: false, command: null, critical: false, required: false },
        ],
      },
    ],
  },
];

// ─── Helper Functions ──────────────────────────────────────────────────────────

/** All steps across all phases, flat array */
export function getAllSteps() {
  return PHASES.flatMap(phase =>
    phase.subgroups.flatMap(sg => sg.steps)
  );
}

/** All steps that are batch-eligible */
export function getBatchSteps() {
  return getAllSteps().filter(s => s.batch);
}

/** Steps belonging to a specific phase */
export function getPhaseSteps(phaseId) {
  const phase = PHASES.find(p => p.id === phaseId);
  if (!phase) return [];
  return phase.subgroups.flatMap(sg => sg.steps);
}

/**
 * Calculate progress for a single phase.
 * @param {string} phaseId - 'setup' | 'customization' | 'delivery'
 * @param {Object} checklistState - JSONB from DB, keyed by step_id
 * @returns {{ done: number, total: number, pct: number, allComplete: boolean }}
 */
export function getPhaseProgress(phaseId, checklistState = {}) {
  const steps = getPhaseSteps(phaseId);
  const total = steps.length;
  const done = steps.filter(s => {
    const state = checklistState[s.id];
    return state === 'done' || state === 'na' ||
      (typeof state === 'object' && (state?.status === 'done' || state?.status === 'na'));
  }).length;
  return { done, total, pct: total > 0 ? Math.round((done / total) * 100) : 0, allComplete: done === total };
}

/**
 * Determine if phase auto-advance conditions are met.
 * @param {string} currentPhase
 * @param {Object} checklistState
 * @param {string|null} deliveryCallScheduledAt
 * @returns {string} - the new phase if auto-advance applies, else currentPhase
 */
export function computeAutoPhase(currentPhase, checklistState = {}, deliveryCallScheduledAt = null) {
  const setupDone = getPhaseProgress('setup', checklistState).allComplete;
  const customDone = getPhaseProgress('customization', checklistState).allComplete;
  const deliveryDone = getPhaseProgress('delivery', checklistState).allComplete;

  if (currentPhase === 'setup' && setupDone) return 'customization';
  if (currentPhase === 'customization' && customDone) return 'awaiting_delivery';
  if (currentPhase === 'awaiting_delivery' && deliveryCallScheduledAt) return 'delivery';
  if (currentPhase === 'delivery' && deliveryDone) return 'deployed';

  // Regression: if steps unchecked, move back
  if (currentPhase === 'deployed' && !deliveryDone) return 'delivery';
  if (currentPhase === 'delivery' && !deliveryCallScheduledAt) return 'awaiting_delivery';
  if (currentPhase === 'awaiting_delivery' && !customDone) return 'customization';
  if (currentPhase === 'customization' && !setupDone) return 'setup';

  return currentPhase;
}

/** Map step_id → phase_id */
export const STEP_PHASE_MAP = (() => {
  const map = {};
  for (const phase of PHASES) {
    for (const sg of phase.subgroups) {
      for (const step of sg.steps) {
        map[step.id] = phase.id;
      }
    }
  }
  return map;
})();

/** All step IDs as a Set for quick lookup */
export const ALL_STEP_IDS = new Set(getAllSteps().map(s => s.id));
